import type { Pagination } from '@heroui/react';
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import type { HTMLAttributes, Key } from 'react';
import { useCallback, useMemo } from 'react';
import { SequentialPagination } from './paging';
import type { EmptyProps } from './empty';
import { Empty } from './empty';

export interface Pagination {
  enabled: boolean;
  absolute?: boolean;
  itemsPerPage?: number;
  allowItemsPerPage?: number[];
  page?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (value: number) => void;
}

export interface Column<T> {
  key: string;
  label: string;
  display: Display<T>;
}

export type Display<T> = ({
  item,
  column,
}: {
  item: T;
  column: Column<T>;
}) => React.ReactElement;

type ItemType = {
  id: string;
  data?: Record<string, unknown>;
};

export interface Props<T extends ItemType>
  extends HTMLAttributes<HTMLDivElement> {
  pagination?: Pagination;
  columns: Column<T>[];
  items: T[];
  count?: number;
  key?: string;
  loading?: boolean;
  empty?: EmptyProps;
}

export function DataTable<T extends ItemType>({
  pagination,
  columns,
  items,
  count,
  key,
  loading,
  empty,
  className,
}: Props<T>) {
  count = count || items.length;
  const itemsPerPage = pagination?.itemsPerPage || 10;
  const page = pagination?.page || 0;
  const onPageChange = pagination?.onPageChange;
  const updatePage = useCallback(
    (page: number) => {
      onPageChange?.(page);
    },
    [onPageChange],
  );
  const onItemsPerPageChange = !pagination?.allowItemsPerPage
    ? undefined
    : pagination.onItemsPerPageChange;
  const columnLookup = useMemo(() => {
    return columns.reduce(
      (m, v) => {
        m[v.key] = v;

        return m;
      },
      {} as Record<string, Column<T>>,
    );
  }, [columns]);
  const visibleItems = useMemo(() => {
    if (!pagination?.enabled) {
      return items;
    }

    return items.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  }, [items, page, itemsPerPage, pagination]);

  const getRowKey = (item: T) => {
    return item[(key || 'id') as keyof T] as Key;
  };

  const CellDisplay = ({ item, col }: { item: T; col: string }) => {
    const column = columnLookup[col];

    return column.display({ item, column });
  };

  return (
    <Table
      isHeaderSticky
      bottomContent={
        !pagination?.enabled ? null : (
          <div className='flex w-full justify-center'>
            <SequentialPagination
              page={page}
              itemsPerPage={itemsPerPage}
              onPageChange={updatePage}
              allowItemsPerPage={pagination.allowItemsPerPage}
              onItemsPerPageChange={onItemsPerPageChange}
              count={count}
              className='fixed bottom-12 shadow-small'
            />
          </div>
        )
      }
      className={`${className || ''} max-h-[calc(100vh-220px)]`}
    >
      <TableHeader columns={columns}>
        {(col) => <TableColumn key={col.key}>{col.label}</TableColumn>}
      </TableHeader>
      <TableBody
        items={visibleItems}
        isLoading={loading}
        emptyContent={<Empty {...empty} />}
      >
        {(item) => (
          <TableRow key={getRowKey(item)}>
            {(col) => (
              <TableCell key={col} className='whitespace-nowrap'>
                <CellDisplay item={item} col={col as string} />
              </TableCell>
            )}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
